'use client';

import { motion } from 'framer-motion';
import {
  Building2, MapPin, BedDouble, Bath, Sofa, Lightbulb,
  Palette, Ruler, ArrowRight, Star, Shield, CheckCircle,
  Sparkles, Layers, Users, Clock, BarChart3, Image, Zap
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { getThemeStyles } from '@/lib/themeStyles';
import Link from 'next/link';

/* ═══════════════════════════════════════════════
   호텔인테리어 포트폴리오 - 디자인인사이트
   호텔·리조트·부티크호텔 인테리어 설계 사례
   ═══════════════════════════════════════════════ */

const HOTEL_PROJECTS = [
  {
    id: 'ID_2319',
    name: '격포관광호텔',
    location: '전북 부안군 변산면',
    type: '관광호텔 리모델링',
    area: '1,657㎡',
    rooms: 38,
    budget: '₩32.5억',
    period: '2025.06 ~ 진행중',
    status: '실증 중',
    features: ['객실 리모델링', '로비/프론트', '레스토랑', '편의시설', '야외테라스'],
    tags: ['호텔인테리어', '관광호텔', '리모델링', '객실디자인'],
    description: '전북 격포항 인근 관광호텔 리모델링 프로젝트. SnapCOST AI로 실시간 공사비 산출 및 도면-내역 검증 적용.',
    color: '#06b6d4',
    youtube: 'https://www.youtube.com/embed/-X5l-lBPV38',
  },
  {
    id: 'DEMO_01',
    name: '부티크호텔 A',
    location: '서울시 종로구',
    type: '부티크호텔 신축',
    area: '890㎡',
    rooms: 22,
    budget: '₩18.7억',
    period: '2025.01 ~ 2025.12',
    status: '완료',
    features: ['개성있는 객실', '루프탑바', '라운지', '스파', '갤러리로비'],
    tags: ['호텔인테리어', '부티크호텔', '신축', '서울'],
    description: '북촌 인근 부티크호텔. 각 층별 다른 콘셉트의 객실 인테리어와 감각적인 공용공간 설계.',
    color: '#f59e0b',
  },
  {
    id: 'DEMO_02',
    name: '비즈니스호텔 B',
    location: '경기 김포시',
    type: '비즈니스호텔 리모델링',
    area: '2,340㎡',
    rooms: 52,
    budget: '₩45.8억',
    period: '2024.03 ~ 2025.02',
    status: '완료',
    features: ['스탠다드객실', '스위트룸', '비즈니스센터', '피트니스', '세미나실'],
    tags: ['호텔인테리어', '비즈니스호텔', '리모델링', '김포'],
    description: '김포공항 인근 비즈니스호텔 전면 리모델링. 효율적인 동선과 프리미엄 비즈니스 공간 설계.',
    color: '#8b5cf6',
  },
  {
    id: 'DEMO_03',
    name: '리조트 C (펜션형)',
    location: '강원 양양군',
    type: '리조트·펜션 복합',
    area: '3,200㎡',
    rooms: 45,
    budget: '₩28.3억',
    period: '2024.06 ~ 2024.12',
    status: '완료',
    features: ['독채펜션', '커뮤니티센터', '수영장', '바베큐장', '조경'],
    tags: ['호텔인테리어', '리조트', '펜션', '강원'],
    description: '자연친화적 리조트 단지. 각 동별 차별화된 인테리어 콘셉트와 외부 공간과의 조화 설계.',
    color: '#22c55e',
  },
];

const HOTEL_SERVICES = [
  {
    title: '객실 인테리어 설계',
    desc: '표준객실부터 스위트룸까지, 브랜드 콘셉트에 맞춘 감각적이고 기능적인 객실 공간 설계',
    icon: BedDouble,
  },
  {
    title: '로비·공용공간 설계',
    desc: '호텔의 첫인상을 결정하는 로비, 라운지, 프론트 데스크 등 공용공간 감성 디자인',
    icon: Sofa,
  },
  {
    title: 'F&B 공간 설계',
    desc: '레스토랑, 바, 라운지, 루프탑 등 식음공간의 분위기와 효율성을 동시에 만족',
    icon: Building2,
  },
  {
    title: '조명·조도 설계',
    desc: '공간별 무드에 최적화된 조명 계획과 자연광 연출로 프리미엄 공간 경험 창출',
    icon: Lightbulb,
  },
  {
    title: '마감재 자재 선정',
    desc: '내구성·유지관리·디자인을 모두 고려한 최적의 마감재 및 가구 자재 추천',
    icon: Palette,
  },
  {
    title: 'AI 공사비 산출',
    desc: 'SnapCOST 엔진으로 도면 기반 실시간 공사비 산출 및 설계 변경 대응',
    icon: BarChart3,
  },
];

const WHY_CHOOSE_US = [
  { metric: '1,200+', label: '실제 설계 프로젝트 경험', icon: Layers },
  { metric: '0건', label: '설계 변경 누락률 (AI 검증)', icon: Shield },
  { metric: '30초', label: '도면↔내역서 자동 검증 시간', icon: Clock },
  { metric: '6.3배', label: '데이터 응답 속도 개선', icon: Zap },
];

export default function HotelInteriorPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const t = getThemeStyles(isDark);

  return (
    <>
      {/* ═══ SEO용 JSON-LD (히든, 검색엔진 전용) ═══ */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'CollectionPage',
                name: '호텔인테리어 포트폴리오 | 디자인인사이트',
                description: '건축 인테리어 설계 전문 기업 디자인인사이트의 호텔·리조트·부티크호텔 인테리어 설계 사례를 확인하세요. AI 기반 공사비 산출, 도면-내역 검증으로 정확성과 효율성을 보장합니다.',
                url: 'https://desinsight.com/portfolio/hotel-interior',
                provider: {
                  '@type': 'Organization',
                  name: '디자인인사이트 주식회사',
                  alternateName: 'Desinsight',
                  url: 'https://desinsight.com',
                },
              },
              ...HOTEL_PROJECTS.map((p) => ({
                '@type': 'Service',
                name: p.name,
                description: p.description,
                provider: { '@type': 'Organization', name: '디자인인사이트' },
                areaServed: p.location,
                category: p.type,
              })),
            ],
          })
        }}
      />

      <main style={{ minHeight: '100vh', paddingTop: 64, background: t.pageBg2, color: t.textWhite }}>
        {/* ── HERO ── */}
        <section style={{
          padding: '80px 24px',
          textAlign: 'center',
          background: isDark
            ? 'linear-gradient(180deg, rgba(6,182,212,0.08) 0%, transparent 100%)'
            : 'linear-gradient(180deg, rgba(6,182,212,0.04) 0%, transparent 100%)',
        }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '6px 16px', borderRadius: 9999,
                background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.2)',
                marginBottom: 24,
              }}>
                <Sparkles style={{ width: 14, height: 14, color: '#06b6d4' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#06b6d4' }}>호텔인테리어 전문</span>
              </div>
              <h1 style={{
                fontSize: 48, fontWeight: 900, lineHeight: 1.15, marginBottom: 16,
                letterSpacing: '-0.02em',
              }}>
                AI로 검증하는<br />
                <span style={{
                  background: 'linear-gradient(135deg, #06b6d4, #22c55e)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  호텔인테리어 설계
                </span>
              </h1>
              <p style={{
                fontSize: 18, color: t.textSecondary, lineHeight: 1.7,
                maxWidth: 600, margin: '0 auto 32px',
              }}>
                디자인인사이트는 호텔·리조트·부티크호텔의 인테리어 설계부터
                AI 기반 공사비 검증, 도면-내역 실시간 정합성까지 원스톱으로 제공합니다.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link href="/"
                  style={{
                    padding: '14px 28px', borderRadius: 12,
                    background: 'linear-gradient(135deg, #06b6d4, #0d9488)',
                    color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                }}>
                  상담 문의 <ArrowRight style={{ width: 16, height: 16 }} />
                </Link>
                <Link href="/ir"
                  style={{
                    padding: '14px 28px', borderRadius: 12,
                    background: t.ghostBg, border: `1px solid ${t.ghostBorder}`,
                    color: t.textSecondary, fontSize: 14, fontWeight: 600, textDecoration: 'none',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                }}>
                  IR 살펴보기
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── 호텔인테리어 설계 서비스 ── */}
        <section style={{ maxWidth: 1280, margin: '0 auto', padding: '64px 24px' }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8, textAlign: 'center' }}>
            호텔인테리어 설계 서비스
          </h2>
          <p style={{ fontSize: 14, color: t.textMuted, textAlign: 'center', marginBottom: 40 }}>
            호텔의 콘셉트와 브랜드 가치를 극대화하는 공간 디자인
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {HOTEL_SERVICES.map((svc) => {
              const Icon = svc.icon;
              return (
                <motion.div
                  key={svc.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  style={{
                    padding: 24, borderRadius: 16,
                    background: t.cardBg, border: `1px solid ${t.borderLight}`,
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: 'rgba(6,182,212,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 16,
                  }}>
                    <Icon style={{ width: 22, height: 22, color: '#06b6d4' }} />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{svc.title}</h3>
                  <p style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.6 }}>{svc.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── 호텔인테리어 프로젝트 사례 ── */}
        <section style={{ maxWidth: 1280, margin: '0 auto', padding: '48px 24px 80px' }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8, textAlign: 'center' }}>
            호텔인테리어 설계 사례
          </h2>
          <p style={{ fontSize: 14, color: t.textMuted, textAlign: 'center', marginBottom: 40 }}>
            디자인인사이트가 직접 설계한 호텔·리조트 프로젝트
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {HOTEL_PROJECTS.map((project) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                style={{
                  borderRadius: 20,
                  background: t.cardBg,
                  border: `1px solid ${t.borderLight}`,
                  overflow: 'hidden',
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  {/* 왼쪽: 프로젝트 정보 */}
                  <div style={{ padding: 32, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 12px', borderRadius: 6,
                      background: `${project.color}15`, color: project.color,
                      fontSize: 11, fontWeight: 700, marginBottom: 12, width: 'fit-content',
                    }}>
                      {project.status === '완료' ? <CheckCircle style={{ width: 12, height: 12 }} /> : <Sparkles style={{ width: 12, height: 12 }} />}
                      {project.status}
                    </div>
                    <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{project.name}</h3>
                    <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MapPin style={{ width: 12, height: 12 }} /> {project.location}
                      </span>
                      <span>|</span>
                      <span>{project.type}</span>
                    </div>
                    <p style={{ fontSize: 13, color: t.textSecondary, lineHeight: 1.6, marginBottom: 16 }}>
                      {project.description}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                      <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: 10, background: `${project.color}08`, border: `1px solid ${project.color}15` }}>
                        <div style={{ fontSize: 11, color: t.textMuted }} { ...{} }>연면적</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: project.color }}>{project.area}</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: 10, background: `${project.color}08`, border: `1px solid ${project.color}15` }}>
                        <div style={{ fontSize: 11, color: t.textMuted }}>객실</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: project.color }}>{project.rooms}실</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: 10, background: `${project.color}08`, border: `1px solid ${project.color}15` }}>
                        <div style={{ fontSize: 11, color: t.textMuted }}>예산</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: project.color }}>{project.budget}</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 4px', borderRadius: 10, background: `${project.color}08`, border: `1px solid ${project.color}15` }}>
                        <div style={{ fontSize: 11, color: t.textMuted }}>기간</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: project.color }}>{project.period}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {project.tags.map((tag) => (
                        <span key={tag} style={{
                          padding: '4px 12px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
                          color: t.textMuted,
                        }}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* 오른쪽: 시각 박스 or 유튜브 영상 */}
                  {'youtube' in project ? (
                    <div style={{
                      background: `linear-gradient(135deg, ${project.color}15, ${project.color}05)`,
                      padding: 16, minHeight: 280,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <div style={{
                        width: '100%', maxWidth: 480, borderRadius: 12,
                        overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
                        aspectRatio: '16/9',
                      }}>
                        <iframe
                          src={(project as any).youtube + '?rel=0&modestbranding=1'}
                          title={project.name + ' 인테리어 영상'}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ width: '100%', height: '100%', border: 'none', borderRadius: 12 }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      background: `linear-gradient(135deg, ${project.color}15, ${project.color}05)`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      padding: 32, minHeight: 280,
                    }}>
                      <div style={{
                        width: 80, height: 80, borderRadius: 20,
                        background: `linear-gradient(135deg, ${project.color}, ${project.color}88)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 16, opacity: 0.9,
                      }}>
                        <Building2 style={{ width: 36, height: 36, color: '#fff' }} />
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                        {project.features.map((f) => (
                          <span key={f} style={{
                            padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
                          }}>
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── WHY 디자인인사이트 ── */}
        <section style={{
          padding: '64px 24px',
          background: isDark
            ? 'linear-gradient(180deg, transparent, rgba(6,182,212,0.04), transparent)'
            : 'linear-gradient(180deg, transparent, rgba(6,182,212,0.02), transparent)',
        }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8, textAlign: 'center' }}>
              왜 디자인인사이트인가
            </h2>
            <p style={{ fontSize: 14, color: t.textMuted, textAlign: 'center', marginBottom: 40 }}>
              AI 기반 검증 시스템으로 설계 품질과 예산 정합성을 동시에 확보합니다
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {WHY_CHOOSE_US.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} style={{
                    padding: 24, borderRadius: 16, textAlign: 'center',
                    background: t.cardBg, border: `1px solid ${t.borderLight}`,
                  }}>
                    <Icon style={{ width: 28, height: 28, color: '#06b6d4', margin: '0 auto 12px' }} />
                    <div style={{ fontSize: 32, fontWeight: 900, marginBottom: 4, background: 'linear-gradient(135deg, #06b6d4, #22c55e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      {item.metric}
                    </div>
                    <div style={{ fontSize: 13, color: t.textMuted }}>{item.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section style={{ maxWidth: 800, margin: '0 auto', padding: '64px 24px 80px', textAlign: 'center' }}>
          <div style={{
            borderRadius: 24, padding: '48px 32px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(6,182,212,0.06), rgba(34,197,94,0.06))'
              : 'linear-gradient(135deg, rgba(6,182,212,0.03), rgba(34,197,94,0.03))',
            border: '1px solid rgba(6,182,212,0.12)',
          }}>
            <h2 style={{ fontSize: 28, fontWeight: 900, marginBottom: 12 }}>
              호텔인테리어, 지금 바로 상담하세요
            </h2>
            <p style={{ fontSize: 15, color: t.textSecondary, marginBottom: 24, lineHeight: 1.7 }}>
              1,200개 이상의 실전 설계 경험과 AI 검증 시스템으로<br />
              정확하고 효율적인 호텔인테리어를 제안합니다.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link href="/"
                style={{
                  padding: '14px 28px', borderRadius: 12,
                  background: 'linear-gradient(135deg, #06b6d4, #0d9488)',
                  color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}>
                무료 상담 신청 <ArrowRight style={{ width: 16, height: 16 }} />
              </Link>
              <Link href="/case/ptk"
                style={{
                  padding: '14px 28px', borderRadius: 12,
                  background: t.ghostBg, border: `1px solid ${t.ghostBorder}`,
                  color: t.textSecondary, fontSize: 14, fontWeight: 600, textDecoration: 'none',
                }}>
                실증 사례 보기
              </Link>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{
          padding: 24, textAlign: 'center', fontSize: 12,
          color: t.textDim, borderTop: `1px solid ${t.borderLight}`,
        }}>
          DesignInsight Inc. — 호텔인테리어 설계 & AI 검증 솔루션 © 2026
        </footer>
      </main>
    </>
  );
}
